import { isServer, type ReactiveController, type ReactiveControllerHost } from "lit";
import EventController from "./event";

export class SlotController implements ReactiveController {
  private _hasConnected = false;
  private _hasUpdated = false;
  private _raf?: number;
  private events: EventController;

  private handleSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;

    if (slot.name === this.name) {
      this.onChange(event);
    }
  };

  private selector: string;

  constructor(
    protected host: ReactiveControllerHost & HTMLElement,
    public name: string = ""
  ) {
    host.addController(this);

    this.events = new EventController(host);
    this.selector = name ? `:scope > [slot="${name}"]` : ":scope > :not([slot])";
  }

  // eslint-disable-next-line no-unused-vars
  protected onChange(_event: Event) {
    this.host.requestUpdate();
  }

  hostConnected() {
    if (isServer) {
      return;
    }

    this._hasConnected = true;

    if (this.host.shadowRoot) {
      this.events.listen(this.host.shadowRoot, "slotchange", this.handleSlotChange);
    }
  }

  hostDisconnected() {
    if (this._raf !== undefined) {
      globalThis.cancelAnimationFrame(this._raf);
    }
  }

  hostUpdated() {
    if (!isServer && this._hasConnected && !this._hasUpdated) {
      // Cancel any pending RAF before scheduling a new one
      if (this._raf !== undefined) {
        globalThis.cancelAnimationFrame(this._raf);
      }
      // Defer until after render to avoid hydration mismatches
      this._raf = globalThis.requestAnimationFrame(() => {
        this._raf = undefined;
        this._hasUpdated = true;
        this.host.requestUpdate();
      });
    }
  }

  get assigned() {
    if (isServer || !this._hasUpdated || !this.host.shadowRoot) {
      return [];
    }

    return Array.from(this.host.querySelectorAll(this.selector));
  }

  get content() {
    if (isServer || !this._hasUpdated || !this.host.shadowRoot) {
      return null;
    }

    return this.host.querySelector(this.selector);
  }

  get isEmpty() {
    return !this.isPresent;
  }

  get isPresent() {
    return this.content != null;
  }

  get tagName() {
    return this.content?.tagName?.toLowerCase() || null;
  }
}

export default SlotController;
